import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { setColor, setFont } from "../../../../assets/styles/styles";
import Button from "../../../../components/common/button/Button";
import Typography from "../../../../components/common/text/Typography";
import Dropdown from "../../../../components/common/inputDropdown/Dropdown";
import Title from "./components/Title";
import Body from "../../layout/Body";
import Card from "./components/Card";
import Modal from "../../components/Modal";
import Banner from "../../../../components/layout/ui/Banner";
import Arrow from "../../../../assets/images/buttonIcons/Arrow.png";

import { getAllNotes, updateNote, getAllNotesByUserId } from "../../../../features/guestbook/api/notesController";
import { CACHE_QUERY_OPTIONS } from "../../../../constants";

const sortByDate = [
    {
        value: "Latest",
    },
    {
        value: "Oldest",
    },
];
const sortByStatus = [
    {
        value: "Published",
    },
    {
        value: "Pending",
    },
];

const UserGuestBookPage = () => {
    const id = useSelector((state) => state.user.userId);
    const [openModal, setOpenModal] = useState(false);
    const [notes, setNotes] = useState(null);
    const [selectedNotes, setSelectedNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [orderFilter, setOrderFilter] = useState("Latest");
    const [statusFilter, setStatusFilter] = useState("Pending");
    const [openBanner, setOpenBanner] = useState(false);
    const [bannerMessage, setBannerMessage] = useState("");
    const [bannerType, setBannerType] = useState("SUCCESS");
    const [isRotated, setIsRotated] = useState(false);
    const navigate = useNavigate();

    const routeChange = (path) => {
        navigate(path);
    };

    const {
        data: notesData,
        loading: loadingData,
        refetch: refecthNotes,
    } = useQuery(["guestBookNotes", id], () => getAllNotesByUserId(id), {
        enabled: !!id,
        ...CACHE_QUERY_OPTIONS(),
    });

    // this does not work because it is sending user_id instead of room id
    const updateNotes = async () => {
        const length = selectedNotes?.length;
        const result = await updateNote(selectedNotes, id, "approve");

        if (result.status === 200) {
            refecthNotes();
            setBannerMessage(
                `You published ${length} notes to your guestbook. You can remove them whenever you'd like.`
            );
        } else {
            setBannerType("ERROR");
            setBannerMessage(`There was an issue publishing the notes. Please try again`);
        }

        setOpenBanner(true);
        setSelectedNotes([]);
    };

    const unApproveNote = async (noteId) => {
        const result = await updateNote([noteId], id, "refuse");
        if (result.status === 200) {
            refecthNotes();
            setBannerType("SUCCESS");
            setBannerMessage("You removed 1 note from your guestbook. Visit your pending notes to republish.");
        } else {
            setBannerType("ERROR");
            setBannerMessage(`There was an issue un-publishing the notes. Please try again`);
        }

        setOpenBanner(true);
        return true;
    };

    const PublishModalContent = ({ updateDeleteModelState }) => (
        <>
            <Typography
                variant="h2PopUpHeading"
                color={setColor.inputText}
                margin={"0 0 2rem 0"}
                textTransform={"none"}
                fontSize={"24px"}
            >
                Publish Notes?
            </Typography>
            <Typography variant="pWhite" margin={"0rem"} fontSize={"16px"}>
                You are about to publish{" "}
                <span style={{ color: `${setColor.spanTextColorThree}`, fontWeight: "bold" }}>
                    {" "}
                    {selectedNotes?.length}
                </span>{" "}
                notes to your guestbook. These will be visible to all visitors in your space.
            </Typography>

            <Button
                variant={"PurpleButton"}
                buttonWidth={"100%"}
                label={"Publish"}
                margin={"2rem 0 1rem 0"}
                onClick={() => {
                    updateNotes();
                    updateDeleteModelState();
                }}
            />

            <Typography
                variant="pWhite"
                onClick={updateDeleteModelState}
                textTransform={"none"}
                margin={"0"}
                fontSize={"15px"}
                FontWeight={"bold"}
            >
                Never Mind
            </Typography>
        </>
    );

    const TotalNoteContent = () => (
        <>
            <Typography variant="ConsoleDescription">
                Total Notes: <strong>{notes?.length}</strong>
            </Typography>
        </>
    );

    const updateSelectedNotes = (id) => {
        if (!selectedNotes.find((note) => note === id)) {
            setSelectedNotes((prev) => [...prev, id]);
        } else {
            setSelectedNotes((prev) => prev.filter((note) => note !== id));
        }
    };

    const filterAndSortNotes = (data, statusFilter, orderFilter) => {
        const filteredNotes = data.filter((note) =>
            statusFilter === "Published" ? note.isApproved === true : note.isApproved === false
        );

        return filteredNotes.sort((a, b) => {
            if (orderFilter === "Latest") {
                return new Date(b.createdAt) - new Date(a.createdAt);
            }

            if (orderFilter === "Oldest") {
                return new Date(a.createdAt) - new Date(b.createdAt);
            }
            return 0;
        });
    };

    useEffect(() => {
        if (notesData) {
            setNotes(filterAndSortNotes(notesData, statusFilter, orderFilter));
            setLoading(false);
        }
    }, [statusFilter, orderFilter, notesData]);

    const updateDeleteModelState = () => {
        setOpenModal((prev) => !prev);
    };

    return (
        <>
            <Banner
                width={"100%"}
                zIndex="1000"
                message={bannerMessage}
                type={bannerType}
                openBanner={openBanner}
                setOpenBanner={setOpenBanner}
                topDisplay
                showCloseButton
                gameScreen={false}
                left={"0"}
                timer={6000}
            />
            <Modal
                width={"550px"}
                isOpen={openModal}
                setIsOpen={updateDeleteModelState}
                children={<PublishModalContent updateDeleteModelState={updateDeleteModelState} />}
            />
            <Title
                description={
                    <>
                        <span
                            style={{
                                color: setColor.brightViolet,
                                cursor: "pointer",
                            }}
                            onClick={() => routeChange("/console/profile")}
                        >
                            Account
                        </span>{" "}
                        {">"} Guestbook
                    </>
                }
                title={"Guestbook"}
                button={
                    <Button
                        buttonWidth="100%"
                        variant={"PurpleButton"}
                        label={selectedNotes?.length > 0 ? `Publish ${selectedNotes.length}` : "Publish"}
                        onClick={updateDeleteModelState}
                        disabled={selectedNotes?.length === 0}
                        status={statusFilter === "Published" ? "none" : "flex"}
                    />
                }
                // navigateBack={notes?.length > 0}
            />
            <TotalNotesWrapper>
                <TotalNoteContent />
            </TotalNotesWrapper>

            <FilterContainer>
                <ButtonContainer>
                    <StatusButton isActive={statusFilter === "Pending"} onClick={() => setStatusFilter("Pending")}>
                        Pending
                    </StatusButton>
                    <StatusButton
                        hasDivisor
                        isActive={statusFilter === "Published"}
                        onClick={() => setStatusFilter("Published")}
                    >
                        Published
                    </StatusButton>

                    <FilterArrow
                        isRotated={isRotated}
                        onClick={() => {
                            setOrderFilter(orderFilter === "Latest" ? "Oldest" : "Latest");
                            setIsRotated((prev) => !prev);
                        }}
                    >
                        <img src={Arrow} alt="Filter Arrow" />
                    </FilterArrow>
                </ButtonContainer>
                <TotalNotesContainer>
                    <TotalNoteContent />
                </TotalNotesContainer>
            </FilterContainer>
            <Body isLoading={loading}>
                {(!notes || notes.length === 0) && (statusFilter === "Pending" || statusFilter === "Published") ? (
                    <>
                        <EmptyMessageComponent>
                            <Typography variant="h2PurpleHeading" margin={"0"}>
                                All Caught Up!
                            </Typography>
                            <Typography variant="pWhite">
                                No pending notes to review. Submitted notes will appear here for your review and
                                publishing.
                            </Typography>
                        </EmptyMessageComponent>
                    </>
                ) : (
                    <CardsContainer>
                        {notes.map((note) => (
                            <div
                                key={note.noteId}
                                onClick={() => {
                                    if (!note.isApproved) updateSelectedNotes(note.noteId);
                                }}
                            >
                                <Card
                                    unApproveNote={unApproveNote}
                                    data={note}
                                    key={note.noteId}
                                    selectedCard={selectedNotes.find((noteID) => noteID === note.noteId)}
                                />
                            </div>
                        ))}
                    </CardsContainer>
                )}
            </Body>
        </>
    );
};

export default UserGuestBookPage;

const CardsContainer = styled.div`
    margin-top: 25px;
    height: auto;
    width: inherit;
    display: grid;
    gap: 13px;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));

    @media (max-width: 1100px) {
        gap: 15px;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    }
    @media (max-width: 940px) {
        gap: 35px;
        grid-template-columns: repeat(auto-fit, minmax(250 px, 1fr));
    }

    @media (max-width: 480px) {
        gap: 10px;
        grid-template-columns: repeat(1, 1fr);
    }
`;

const FilterContainer = styled.div`
    margin-bottom: 20px;
    display: flex;
    flex-direction: row;
    width: 100%;
    margin-top: 14px;
    align-items: center;
    justify-content: space-between;
`;

const ButtonContainer = styled.div`
    margin-top: 10px;
    display: flex;
    gap: 15px;
`;

const StatusButton = styled.button`
    position: relative;
    padding: 0 16px;
    height: 38px;
    border-radius: 25px;
    border: ${(props) =>
        props.isActive ? `2px solid ${setColor.brightViolet}` : `2px solid ${setColor.brightVioletTransparent}`};
    cursor: pointer;
    font-size: 18px;
    font-family: ${setFont.barlow};
    letter-spacing: -0.5px;
    font-weight: ${(props) => (props.isActive ? "bold" : "400")};
    background-color: ${(props) => (props.isActive ? setColor.opaquePurple : "transparent")};
    color: ${(props) => (props.isActive ? setColor.brightViolet : setColor.inputText)};
    transition:
        border 0.3s,
        color 0.3s;

    &:hover {
        background-color: ${setColor.opaquePurple};
    }

    &::before {
        content: ${(props) => (props.hasDivisor ? '""' : "none")}; /* Conditionally render the pseudo-element */
        position: absolute;
        right: -21px; /* Position the divisor to the right of the button */
        width: 1px;
        height: 80%;
        background-color: ${setColor.brightVioletTransparent};
    }
`;

const TotalNotesWrapper = styled.div`
    display: none;

    @media (max-width: 768px) {
        display: flex;
        justify-content: center;
        font-size: 14px;
    }
`;

const TotalNotesContainer = styled.div`
    @media (max-width: 768px) {
        display: none;
    }
`;

const FilterArrow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: ${setColor.opaquePurple};
    cursor: pointer;
    transition:
        transform 0.3s ease,
        background-color 0.3s ease;
    margin-left: 20px;

    transform: ${(props) => (props.isRotated ? "rotate(0deg)" : "rotate(180deg)")}; /* Rotate based on state */

    &:hover {
        background-color: ${setColor.brightVioletTransparent};
    }

    img {
        width: 20px;
        height: auto;
    }
`;

const EmptyMessageComponent = styled.div`
    width: 600px;
    justify-content: space-between;
    height: auto;
    border-radius: 20px;
    background: ${setColor.passwordValidBox};
    padding: 2rem;
    margin: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;

    @media (max-width: 768px) {
        padding: 1rem;
        width: 100%;
    }
`;
